import {
  Box, Button, IconButton, Typography
} from '@mui/material'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AssigneesCell } from '../../../../components/checklists/result/folders/NewDesignCells'
import { TOKENS } from '../../../dashboard-v2/components/primitives'

// The frame of the checklist view in the new design (design draft screen 3): header, areas as steps
// with progress on the left, the open area in the middle, a fixed bar at the bottom. It only shows
// what ResultView passes in; answering, rules, saving and closing stay in ResultView / ResultArea.

export interface FrameArea {
  key: string
  id: number | string
  position: number
  title: string
  filled: number
  total: number
  deviations: number
  canOpen: boolean
  active: boolean
}

interface Props {
  // page: full page with the area column (draft 3); panel: next to the list or on a phone, areas as
  // tabs under the header and the bar at the bottom of the panel (drafts 1b, 1c, 4)
  variant?: 'page' | 'panel'
  onExpand?: (() => void) | null
  // page: back to the view next to the list (drafts 1b / 1c)
  onCollapse?: (() => void) | null
  onCloseView?: (() => void) | null
  title: string
  meta: string
  chips: Array<{ label: string, tone: 'neutral' | 'primary' | 'warning' | 'danger' }>
  assignees: any[]
  menu?: ReactNode
  // null: no way back (shared-link visitor)
  onBack: (() => void) | null
  areas: FrameArea[]
  onSelectArea: (id: number | string, position: number) => void
  filled: number
  total: number
  deviations: number
  requiredMissing: number
  onPrev?: (() => void) | null
  onNext?: (() => void) | null
  nextTitle?: string | null
  onCloseProcess?: (() => void) | null
  closeLabel?: string
  closeDisabled?: boolean
  extraActions?: ReactNode
  children: ReactNode
}

const CHIP_TONES = {
  neutral: { bg: '#F1F3F6', fg: TOKENS.muted },
  primary: { bg: TOKENS.primarySoft, fg: TOKENS.primaryDark },
  warning: { bg: '#FFF4E0', fg: '#8A5300' },
  danger: { bg: '#FDECEC', fg: '#A3261F' }
}

function Ring({ pct }: { pct: number }) {
  const r = 27
  const c = 2 * Math.PI * r
  return (
    <Box sx={{ position: 'relative', width: 64, height: 64, flex: 'none' }}>
      <Box
        component="span"
        sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: TOKENS.text
        }}
      >
        {`${pct} %`}
      </Box>
    <svg width="64" height="64" viewBox="0 0 64 64" role="img" aria-label={`${pct} %`}>
      <circle cx="32" cy="32" r={r} fill="none" stroke="#E6EAF0" strokeWidth="7" />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke={pct >= 100 ? '#1E7A4C' : TOKENS.primary}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${(c * pct) / 100} ${c}`}
        transform="rotate(-90 32 32)"
      />
    </svg>
    </Box>
  )
}

function StepIcon({ area, index }: { area: FrameArea, index: number }) {
  if (area.deviations > 0) {
    return (
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%', bgcolor: '#FDECEC', color: '#A3261F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flex: 'none'
      }}
      >
        !
      </Box>
    )
  }
  if (area.total > 0 && area.filled >= area.total) {
    return (
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%', bgcolor: '#1E7A4C', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none'
      }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </Box>
    )
  }
  return (
    <Box sx={{
      width: 28, height: 28, borderRadius: '50%', border: '2px solid #C3CCD8', color: TOKENS.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flex: 'none', boxSizing: 'border-box'
    }}
    >
      {index + 1}
    </Box>
  )
}

export default function ChecklistFrame(props: Props) {
  const {
    variant = 'page', onCollapse,
    title, meta, chips, assignees, menu, onBack, areas, onSelectArea, filled, total, deviations, requiredMissing,
    onPrev, onNext, nextTitle, onCloseProcess, closeLabel, closeDisabled, extraActions, children
  } = props
  if (variant === 'panel') {
    return (
      <PanelLayout
        {...props}
        pct={total > 0 ? Math.min(100, Math.round((filled / total) * 100)) : 0}
      />
    )
  }
  const { t } = useTranslation()
  const pct = total > 0 ? Math.min(100, Math.round((filled / total) * 100)) : 0

  return (
    <Box className="nd-frame" sx={{ minHeight: '100%', bgcolor: TOKENS.ground, pb: '96px' }}>
      <Box
        component="header"
        sx={{
          bgcolor: '#FFFFFF', borderBottom: '1px solid #C3CCD8', px: { md: 4, lg: 5 }, py: 2.25, display: 'flex', alignItems: 'center', gap: 2.5
        }}
      >
        {onBack && (
        <IconButton
          aria-label={t('common.back')}
          onClick={onBack}
          sx={{
            width: 40, height: 40, borderRadius: '10px', border: '1px solid #C3CCD8', color: TOKENS.text
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'
          }}
          >
            <Typography component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 700, color: TOKENS.text }}>{title}</Typography>
            {chips.map((chip) => (
              <Box
                key={chip.label}
                component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '999px', fontSize: 11.5, fontWeight: 700, bgcolor: CHIP_TONES[chip.tone].bg, color: CHIP_TONES[chip.tone].fg
                }}
              >
                {chip.label}
              </Box>
            ))}
          </Box>
          {meta && <Typography sx={{ fontSize: 13, color: TOKENS.muted, mt: 0.5 }}>{meta}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}><AssigneesCell assignees={assignees} /></Box>
        {onCollapse && (
          <IconButton
            aria-label={t('checklistFrame.collapse')}
            title={t('checklistFrame.collapse')}
            onClick={onCollapse}
            sx={{
              width: 36, height: 36, borderRadius: '9px', border: '1px solid #C3CCD8', color: TOKENS.text
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </IconButton>
        )}
        {menu && <Box className="nd-frame-menu">{menu}</Box>}
      </Box>

      <Box sx={{
        display: 'flex', gap: 4, px: { md: 4, lg: 5 }, py: 3.5, alignItems: 'flex-start'
      }}
      >
        <Box component="aside" sx={{ width: 300, flex: 'none', display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16 }}>
          <Box sx={{
            bgcolor: '#FFFFFF', border: '1px solid #C3CCD8', borderRadius: '12px', p: 2.25, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 1px 3px rgba(16,24,40,.10)'
          }}
          >
            <Ring pct={pct} />
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{t('processList.answered', { answered: filled, total })}</Typography>
              {deviations > 0 && (
                <Typography sx={{ fontSize: 13, color: '#A3261F', fontWeight: 600, mt: 0.25 }}>{t('processList.deviations', { count: deviations })}</Typography>
              )}
              {requiredMissing > 0 && (
                <Typography sx={{ fontSize: 13, color: TOKENS.muted, mt: 0.25 }}>{t('checklistFrame.requiredOpen', { count: requiredMissing })}</Typography>
              )}
            </Box>
          </Box>
          <Box
            component="nav"
            aria-label={t('checklistFrame.areas')}
            sx={{
              bgcolor: '#FFFFFF', border: '1px solid #C3CCD8', borderRadius: '12px', p: 1, display: 'flex', flexDirection: 'column', gap: 0.25, boxShadow: '0 1px 3px rgba(16,24,40,.10)'
            }}
          >
            {areas.map((area, index) => (
              <Box
                key={area.key}
                component="button"
                type="button"
                disabled={!area.canOpen}
                onClick={() => onSelectArea(area.id, area.position)}
                aria-current={area.active ? 'step' : undefined}
                sx={{
                  all: 'unset',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: '10px',
                  cursor: area.canOpen ? 'pointer' : 'not-allowed',
                  opacity: area.canOpen ? 1 : 0.5,
                  bgcolor: area.active ? '#EEF4FC' : 'transparent',
                  '&:hover': { bgcolor: area.active ? '#EEF4FC' : '#F7F9FC' },
                  '&:focus-visible': { outline: `2px solid ${TOKENS.primary}` }
                }}
              >
                <StepIcon area={area} index={index} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 14, fontWeight: area.active ? 700 : 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}
                  >
                    {area.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: area.deviations ? '#A3261F' : TOKENS.muted }}>
                    {area.total > 0 ? t('processList.answered', { answered: area.filled, total: area.total }) : ''}
                    {area.deviations > 0 ? ` · ${t('processList.deviations', { count: area.deviations })}` : ''}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="main" sx={{ flex: 1, minWidth: 0, maxWidth: 860 }}>
          {children}
        </Box>
      </Box>

      <Box
        component="footer"
        className="nd-checklist-footer"
        sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, bgcolor: '#FFFFFF', borderTop: '1px solid #C3CCD8', boxShadow: '0 -2px 10px rgba(16,24,40,.06)', pl: { md: 4, lg: 5 }, pr: '96px', py: 1.75, display: 'flex', alignItems: 'center', gap: 2.5
        }}
      >
        <Box sx={{ width: 300, flex: 'none' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('processList.answered', { answered: filled, total })}</Typography>
          <Box sx={{
            height: 6, borderRadius: '3px', bgcolor: '#D7DDE6', mt: 0.75, overflow: 'hidden'
          }}
          >
            <Box sx={{ height: 6, width: `${pct}%`, bgcolor: pct >= 100 ? '#1E7A4C' : TOKENS.primary }} />
          </Box>
        </Box>
        <Typography sx={{
          flex: 1, fontSize: 13.5, fontWeight: 600, color: requiredMissing > 0 ? '#A3261F' : TOKENS.muted
        }}
        >
          {requiredMissing > 0 ? t('checklistFrame.stillOpen', { count: requiredMissing }) : ''}
        </Typography>
        {extraActions}
        {onPrev && (
          <Button
            onClick={onPrev}
            sx={{
              height: 48, px: 2.75, borderRadius: '10px', border: '1px solid #C3CCD8', color: TOKENS.text, textTransform: 'none', fontSize: 15, fontWeight: 600
            }}
          >
            {t('common.back')}
          </Button>
        )}
        {onNext ? (
          <Button
            variant="contained"
            disableElevation
            onClick={onNext}
            sx={{
              height: 48, px: 3.25, borderRadius: '10px', textTransform: 'none', fontSize: 15, fontWeight: 600
            }}
          >
            {nextTitle ? t('checklistFrame.nextTo', { title: nextTitle }) : t('checklistFrame.next')}
          </Button>
        ) : (onCloseProcess && (
          <Button
            variant="contained"
            disableElevation
            onClick={onCloseProcess}
            disabled={closeDisabled}
            sx={{
              height: 48, px: 3.25, borderRadius: '10px', textTransform: 'none', fontSize: 15, fontWeight: 600
            }}
          >
            {closeLabel}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

// drafts 1b / 1c (next to the list) and 4 (phone)
function PanelLayout(props: Props & { pct: number }) {
  const {
    onExpand, onCloseView, title, meta, chips, assignees, menu, onBack, areas, onSelectArea, filled, total, deviations,
    requiredMissing, onPrev, onNext, nextTitle, onCloseProcess, closeLabel, closeDisabled, extraActions, children, pct
  } = props
  const { t } = useTranslation()
  const iconButton = {
    width: 36, height: 36, borderRadius: '9px', border: '1px solid #C3CCD8', color: TOKENS.text, flex: 'none'
  }
  return (
    <Box className="nd-frame nd-frame-panel" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#F4F6F9' }}>
      <Box
        component="header"
        sx={{
          bgcolor: '#FFFFFF', borderBottom: '1px solid #E1E6EE', px: { xs: 2, sm: 3 }, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {!onCloseView && onBack && (
            <IconButton aria-label={t('common.back')} onClick={onBack} sx={{ ...iconButton, ml: -0.5 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'
            }}
            >
              <Typography component="h1" sx={{ m: 0, fontSize: { xs: 17, sm: 19 }, fontWeight: 700, color: TOKENS.text }}>{title}</Typography>
              {chips.map((chip) => (
                <Box
                  key={chip.label}
                  component="span"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '999px', fontSize: 11.5, fontWeight: 700, bgcolor: CHIP_TONES[chip.tone].bg, color: CHIP_TONES[chip.tone].fg
                  }}
                >
                  {chip.label}
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.muted, mt: 0.25 }}>
              {[meta, t('processList.answered', { answered: filled, total })].filter(Boolean).join(' · ')}
              {deviations > 0 && (
                <Box component="span" sx={{ color: '#A3261F', fontWeight: 600 }}>{` · ${t('processList.deviations', { count: deviations })}`}</Box>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}><AssigneesCell assignees={assignees} /></Box>
          {onExpand && (
            <IconButton aria-label={t('checklistFrame.expand')} title={t('checklistFrame.expand')} onClick={onExpand} sx={iconButton}>
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          )}
          {menu && <Box className="nd-frame-menu">{menu}</Box>}
          {onCloseView && (
            <IconButton aria-label={t('common.close')} onClick={onCloseView} sx={{ ...iconButton, border: 0, bgcolor: '#F1F3F6' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
            </IconButton>
          )}
        </Box>
        <Box component="nav" aria-label={t('checklistFrame.areas')} sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.25 }}>
          {areas.map((area) => {
            const done = area.total > 0 && area.filled >= area.total
            const dev = area.deviations > 0
            return (
              <Box
                key={area.key}
                component="button"
                type="button"
                disabled={!area.canOpen}
                onClick={() => onSelectArea(area.id, area.position)}
                aria-current={area.active ? 'step' : undefined}
                sx={{
                  all: 'unset',
                  flex: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 34,
                  px: 1.5,
                  borderRadius: '999px',
                  boxSizing: 'border-box',
                  cursor: area.canOpen ? 'pointer' : 'not-allowed',
                  opacity: area.canOpen ? 1 : 0.5,
                  fontSize: 13,
                  fontWeight: area.active ? 700 : 600,
                  whiteSpace: 'nowrap',
                  border: dev ? '2px solid #C62828' : (area.active ? `2px solid ${TOKENS.primary}` : '1px solid #C3CCD8'),
                  bgcolor: dev ? '#FDECEC' : (area.active ? TOKENS.primarySoft : '#FFFFFF'),
                  color: dev ? '#A3261F' : (done ? '#1E7A4C' : TOKENS.text),
                  '&:focus-visible': { outline: `2px solid ${TOKENS.primary}` }
                }}
              >
                {dev && <span style={{ fontWeight: 800 }}>!</span>}
                {!dev && done && <span aria-hidden>✓</span>}
                {area.title}
                {area.total > 0 && <span style={{ fontWeight: 500, opacity: 0.8 }}>{`${area.filled}/${area.total}`}</span>}
              </Box>
            )
          })}
        </Box>
      </Box>

      <Box component="main" sx={{ flex: 1, px: { xs: 1.5, sm: 3 }, py: 2 }}>
        {children}
      </Box>

      <Box
        component="footer"
        className="nd-panel-footer nd-checklist-footer"
        sx={{
          position: 'sticky', bottom: 0, zIndex: 20, bgcolor: '#FFFFFF', borderTop: '1px solid #C3CCD8', pl: { xs: 1.5, sm: 3 }, pr: { xs: 1.5, sm: 3, md: '96px' }, py: 1.25, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: { xs: 1, md: 1.75 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
          <Box sx={{ width: { xs: 'auto', md: 140 }, flex: { xs: 1, md: 'none' } }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, display: { xs: 'none', md: 'block' } }}>{t('processList.answered', { answered: filled, total })}</Typography>
            <Box sx={{ height: 6, borderRadius: '3px', bgcolor: '#D7DDE6', mt: { md: 0.5 }, overflow: 'hidden' }}>
              <Box sx={{ height: 6, width: `${pct}%`, bgcolor: pct >= 100 ? '#1E7A4C' : TOKENS.primary }} />
            </Box>
          </Box>
          <Typography sx={{
            fontSize: 12.5, fontWeight: 600, color: requiredMissing > 0 ? '#A3261F' : TOKENS.muted, whiteSpace: 'nowrap'
          }}
          >
            {requiredMissing > 0 ? t('checklistFrame.stillOpen', { count: requiredMissing }) : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {extraActions}
          {onPrev && (
            <Button
              onClick={onPrev}
              sx={{
                flex: { xs: 1, md: 'none' }, height: 44, px: 2.25, borderRadius: '10px', border: '1px solid #C3CCD8', color: TOKENS.text, textTransform: 'none', fontSize: 14, fontWeight: 600
              }}
            >
              {t('common.back')}
            </Button>
          )}
          {onNext ? (
            <Button
              variant="contained"
              disableElevation
              onClick={onNext}
              sx={{
                flex: { xs: 2, md: 'none' }, height: 44, px: 2.5, borderRadius: '10px', textTransform: 'none', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap'
              }}
            >
              {nextTitle ? t('checklistFrame.nextTo', { title: nextTitle }) : t('checklistFrame.next')}
            </Button>
          ) : (onCloseProcess && (
            <Button
              variant="contained"
              disableElevation
              onClick={onCloseProcess}
              disabled={closeDisabled}
              sx={{
                flex: { xs: 2, md: 'none' }, height: 44, px: 2.5, borderRadius: '10px', textTransform: 'none', fontSize: 14, fontWeight: 600
              }}
            >
              {closeLabel}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
